import React, { useCallback, useEffect, useRef, useState } from "react";
import clsx from "clsx";

import { useTranslation } from "lib/i18n/use-translation";
import i18n, { I18N_ENABLED } from "lib/i18n";
import { logError } from "utility/Function";
import {
  SUPPORTED_LOCALES,
  SupportedLocale,
  resolveSupportedLocale,
} from "utility/locale";
import {
  readLanguageChoice,
  resolveActiveLocale,
  writeLanguageChoice,
} from "utility/languagePreference";

import flagEnGB from "../../../../packages/assets/flags/flag-en-GB.svg";
import flagFrFR from "../../../../packages/assets/flags/flag-fr-FR.svg";
import flagPtPT from "../../../../packages/assets/flags/flag-pt-PT.svg";
import flagEsES from "../../../../packages/assets/flags/flag-es-ES.svg";

import styles from "./language-menu.module.scss";

/**
 * Drawn rather than emoji, and shared with mobile
 * (`frontend/packages/assets/flags`). Windows browsers ship no flag glyphs at
 * all — Segoe UI Emoji renders `🇬🇧` as the letters "GB" — so an emoji here
 * would be unrecognisable for a large share of web learners.
 */
const FLAGS: Record<SupportedLocale, string> = {
  "en-GB": flagEnGB,
  "fr-FR": flagFrFR,
  "pt-PT": flagPtPT,
  "es-ES": flagEsES,
};

export interface LanguageMenuProps {
  /**
   * `menu` — a flag button that opens a floating list. Used in chrome where
   * there is no room to grow, e.g. the auth top bar.
   * `accordion` — reads as an ordinary row ("Language: 🏴") and expands in
   * place. Used in the sidebar, where a floating panel sat awkwardly against
   * the surrounding nav entries.
   */
  variant?: "menu" | "accordion";
  /** Applied to the trigger so a host can lend it its own row styling. */
  triggerClassName?: string;
  /** Applied to each option, for the same reason. */
  itemClassName?: string;
  /**
   * The signed-in learner's stored language, when there is one. Omitted on
   * the auth screens, where the choice can only be local.
   */
  profileLanguage?: string | null;
  /**
   * Persist the choice to the profile. Omitted when signed out — the choice
   * is then held locally as pending and the auth provider pushes it at the
   * first authenticated moment.
   *
   * Injected rather than read from auth context so this component carries no
   * provider dependency: it is mounted in the shared auth shell, which
   * several screens render with neither an Apollo client nor an AuthProvider.
   */
  onChoose?: (locale: SupportedLocale) => Promise<void>;
  /** Where the list opens relative to the trigger. */
  placement?: "below-end" | "above-start";
  className?: string;
}

/**
 * Compact language switcher: a flag button that opens the four supported
 * languages.
 *
 * Works signed out as well as in — on the sign-in screen there is no profile
 * to write to, so the choice is stored locally as pending and pushed to the
 * profile at the first authenticated moment (see `languagePreference`).
 * Renders nothing when localisation is build-gated off.
 */
const LanguageMenu: React.FC<LanguageMenuProps> = ({
  profileLanguage,
  onChoose,
  variant = "menu",
  triggerClassName,
  itemClassName,
  placement = "below-end",
  className,
}) => {
  const { t, i18n: i18nInstance } = useTranslation(["settings", "common"]);
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // Track the language actually rendering, not the stored choice. Storage is
  // not reactive, so reading it here left the flag a change behind: selecting
  // re-renders (the menu closes) before the write lands, and nothing
  // re-renders afterwards. `useTranslation` re-renders on `languageChanged`,
  // so this updates the moment the copy does — and stays right when the
  // language is changed from elsewhere, e.g. `LocaleSync` on sign-in.
  const active =
    resolveSupportedLocale(i18nInstance.language) ??
    resolveActiveLocale({ stored: readLanguageChoice(), profileLanguage });

  // Close on an outside click or Escape — a menu that traps the page is worse
  // than no menu, and this one sits in chrome the user is passing through.
  useEffect(() => {
    if (!open || variant === "accordion") return;

    const onPointerDown = (event: MouseEvent): void => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent): void => {
      if (event.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open, variant]);

  const handleSelect = useCallback(
    async (locale: SupportedLocale): Promise<void> => {
      setOpen(false);
      if (locale === active) return;

      // Render in the new language first: it is local, instant, and the thing
      // the user asked for. The network write is allowed to lag behind it.
      try {
        await i18n.changeLanguage(locale);
      } catch (error) {
        logError("Failed to switch locale (non-fatal)", error as Error);
      }

      // Signed out — the sign-in screen — there is no profile to write to, so
      // hold the choice and let the auth provider push it once there is one.
      writeLanguageChoice({ locale, pendingSync: !onChoose });
      if (!onChoose) return;

      try {
        await onChoose(locale);
      } catch (error) {
        // Keep the choice pending so the next sign-in retries it, rather than
        // losing a deliberate choice to a dropped connection.
        writeLanguageChoice({ locale, pendingSync: true });
        logError("Saving the language choice failed", error as Error);
      }
    },
    [active, onChoose]
  );

  if (!I18N_ENABLED) return null;

  const optionsFor = (locales: readonly SupportedLocale[]): JSX.Element[] =>
    locales.map((locale) => (
      <li key={locale} role="none">
        <button
          type="button"
          role="menuitemradio"
          aria-checked={locale === active}
          className={clsx(
            styles["item"],
            itemClassName,
            locale === active && styles["item-active"]
          )}
          onClick={() => {
            void handleSelect(locale);
          }}
        >
          <img className={styles["flag"]} src={FLAGS[locale]} alt="" />
          <span>{t(`settings:language.names.${locale}`)}</span>
        </button>
      </li>
    ));

  if (variant === "accordion") {
    return (
      <div className={clsx(styles["accordion"], className)}>
        <button
          type="button"
          className={clsx(styles["accordion-trigger"], triggerClassName)}
          aria-expanded={open}
          onClick={() => setOpen((wasOpen) => !wasOpen)}
        >
          <img className={styles["flag"]} src={FLAGS[active]} alt="" />
          <span>{t(`settings:language.names.${active}`)}</span>
        </button>
        {open && (
          <ul className={styles["accordion-list"]} role="menu">
            {optionsFor(SUPPORTED_LOCALES.filter((l) => l !== active))}
          </ul>
        )}
      </div>
    );
  }

  return (
    <div ref={containerRef} className={clsx(styles["container"], className)}>
      <button
        type="button"
        className={styles["trigger"]}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={t("settings:language.title")}
        onClick={() => setOpen((wasOpen) => !wasOpen)}
      >
        <img className={styles["flag"]} src={FLAGS[active]} alt="" />
      </button>

      {open && (
        <ul className={clsx(styles["menu"], styles[placement])} role="menu">
          {optionsFor(SUPPORTED_LOCALES)}
        </ul>
      )}
    </div>
  );
};

export default LanguageMenu;
